import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  Field,
  Filters,
  linkTextClasses,
  Modal,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  Table,
  type Action,
} from '@evogard/ui';
import type { ErpCandidate, MemberStatus, TenantMember } from '@evogard/contract';
import {
  canAccess,
  documentLabel,
  formatDocument,
  formatInstant,
  loadFailureMessage,
} from '@evogard/domain';
import { api, errorMessage, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { IdentityFields } from '../../components/UserIdentityForm.js';

/**
 * Quem tem acesso ao portal, nesta base.
 *
 * É a tela que responde "não consigo entrar no portal" sem ninguém abrir o
 * banco — hoje esse pedido chega ao suporte e termina num `UPDATE` escrito à
 * mão por quem tiver a credencial do Postgres.
 *
 * **O CPF sai mascarado aqui.** O documento inteiro está no detalhe, e essa
 * separação tem um motivo concreto: abrir um cadastro é um ato pontual e
 * auditável; uma listagem paginada de duzentos não é.
 */

const MEMBER_STATUSES: ReadonlyArray<{ value: MemberStatus; label: string }> = [
  { value: 'ativo', label: 'Com acesso' },
  { value: 'convidado', label: 'Convite pendente' },
  { value: 'sem_acesso', label: 'Sem acesso' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

export function MemberStatusBadge({ situacao }: { situacao: MemberStatus }) {
  /*
   * `sem_acesso` é `alerta` e não `neutro`: é a situação que pede uma ação da
   * operação — a pessoa está vinculada à base e não consegue entrar. Cinza a
   * faria parecer um estado de repouso.
   */
  // @valor
  const tones = {
    ativo: 'sucesso',
    convidado: 'informacao',
    sem_acesso: 'alerta',
    bloqueado: 'erro',
  } as const;

  // @valor
  const labels = {
    ativo: 'Com acesso',
    convidado: 'Convite pendente',
    sem_acesso: 'Sem acesso',
    bloqueado: 'Bloqueado',
  } as const;

  return <Badge tone={tones[situacao]}>{labels[situacao]}</Badge>;
}

export function MembersScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();

  const canGrant = canAccess(context?.permissions ?? [], 'portal.members', 'criar');

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [situacao, setStatus] = useState<MemberStatus | ''>('');
  /*
   * "Dar acesso" passou a nascer FECHADO, atrás do botão do cabeçalho.
   *
   * Ele era um cartão permanente entre o título e a lista, com um campo de
   * documento e um botão — e, com a busca e a situação soltos logo abaixo, a
   * tela abria com três caixas de texto empilhadas que pareciam a mesma barra
   * de filtros. Duas recortavam a lista e a terceira criava um registro: o
   * gesto mais perigoso da tela era o que menos se distinguia dos outros dois.
   *
   * É a mesma forma de "Abrir demanda" e "Registrar venda" — o ato mora no
   * cabeçalho, e o formulário aparece quando alguém o pede.
   */
  const [granting, setGranting] = useState(false);

  /*
   * Até 15/09 a lista vinha com `page: 1, perPage: 50` e o aviso de corte
   * embaixo do título. O aviso existia porque não havia como virar página.
   */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  /* Filtrar encurta a lista, e a página em que se estava deixa de existir. */
  useEffect(() => {
    setPage(1);
  }, [tenantId, term, situacao, perPage]);

  const membersQuery = useQuery({
    queryKey: ['portal', 'associados', tenantId, term, situacao, page, perPage],
    queryFn: async () => {
      const r = await api.members.list({
        query: {
          page,
          perPage,
          search: term || undefined,
          situacao: situacao || undefined,
        },
      });
      // a frase sai do status: 403 não pode virar "tente de novo"
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os associados'));
      }
      return r.body;
    },
    // segurar o resultado anterior só vale dentro da mesma base
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const total = membersQuery.data?.total;

  const canEdit = can(context, 'portal.members', 'editar');
  const canInvite = can(context, 'portal.members', 'convidar');
  const canBlock = can(context, 'portal.members', 'bloquear');

  const [error, setError] = useState<string | null>(null);
  /** O aviso do convite enviado — a data vem da resposta, não de um número escrito aqui. */
  const [sent, setSent] = useState<string | null>(null);

  /* a ficha aberta lê `['portal', 'associado', …]`, e a lista também muda */
  const reload = () => void client.invalidateQueries({ queryKey: ['portal'] });

  const invite = useMutation({
    mutationFn: async (a: TenantMember) => {
      const r = await api.members.invite({ params: { id: a.id }, body: {} });
      if (r.status !== 201) {
        throw new Error(errorMessage(r.body, 'Não foi possível enviar o convite.'));
      }
      return { email: a.email, expiresAt: r.body.expiresAt };
    },
    onSuccess: ({ email, expiresAt }) => {
      setError(null);
      setSent(`${email} recebeu o link. Ele vale até ${formatInstant(expiresAt)}.`);
      reload();
    },
    onError: (e: Error) => {
      setSent(null);
      setError(e.message);
    },
  });

  const block = useMutation({
    mutationFn: async ({ id, blocked }: { id: string; blocked: boolean }) => {
      const r = await api.members.block({ params: { id }, body: { blocked } });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível alterar o bloqueio.'));
      }
    },
    onSuccess: () => {
      setError(null);
      setSent(null);
      reload();
    },
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Guardado pelo ID, e o associado derivado da lista a cada render, como em
   * Usuários: a lista é invalidada ao salvar, e uma cópia capturada na
   * abertura mostraria o nome de antes.
   */
  const [editingIdentityId, setEditingIdentityId] = useState<string | null>(null);
  const editingIdentityMember =
    membersQuery.data?.items.find((a) => a.id === editingIdentityId) ?? null;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Associados do portal"
        actions={
          /* Esconder é cortesia; quem protege é o guard. A rota continua
             exigindo `portal.members:criar`. */
          canGrant && (
            <Button icon="mais" onClick={() => setGranting(true)}>
              Dar acesso
            </Button>
          )
        }
      >
        Quem tem acesso ao portal em {context?.currentTenant.nome}.
      </PageHeader>

      {granting && canGrant && (
        <GrantAccess
          created={() => client.invalidateQueries({ queryKey: ['portal', 'associados'] })}
          toClose={() => setGranting(false)}
        />
      )}

      {membersQuery.isError && <Alert tone="erro">{membersQuery.error.message}</Alert>}

      {/* a recusa vem do servidor com a frase dele */}
      {error && <Alert tone="erro">{error}</Alert>}

      {sent && (
        <Alert tone="sucesso" title="Convite enviado.">
          {sent} O anterior, se havia, deixou de valer.
        </Alert>
      )}

      {/*
       * Erro sem dado nenhum: o aviso é a resposta inteira. A tabela anunciaria
       * "ninguém tem acesso" logo abaixo dele, e essa é a afirmação que parece
       * um fato.
       */}
      {(!membersQuery.isError || membersQuery.data) && (
        <Card title={total === undefined ? 'Carregando…' : labelTotal(total)} bleed>
          <Table
            paginate={false}
            /*
             * A busca na barra e a situação no painel — a divisão de 17/09. A
             * busca filtra enquanto se digita porque é o recorte cujo resultado
             * a pessoa usa para decidir o que digitar em seguida; a situação
             * espera o "Filtrar".
             */
            search={{
              label: 'Pesquisar associados',
              placeholder: 'Nome, E-mail ou Código do ERP',
              value: search,
              onChange: setSearch,
            }}
            toolbar={
              <Filters
                values={{ situacao }}
                onApply={(values) =>
                  setStatus(String(values.situacao ?? '') as MemberStatus | '')
                }
                fields={[
                  {
                    type: 'selecao',
                    name: 'situacao',
                    label: 'Situação',
                    placeholder: 'Todas',
                    options: MEMBER_STATUSES,
                  },
                ]}
              />
            }
            label="Associados do portal"
            items={membersQuery.data?.items ?? []}
            loading={membersQuery.isLoading}
            rowKey={(a) => a.id}
            empty={
              term || situacao
                ? 'Ninguém com esses filtros.'
                : 'Nenhum associado vinculado a esta base ainda.'
            }
            rowTitle={(a) => a.nome}
            rowSubtitle={(a) => a.email}
            rowIcon="pessoa"
            /*
             * O painel de ações de Usuários (03/11/2026), sem departamentos e
             * sem exceções — associado não tem nenhum dos dois. Vincular ao ERP
             * e derrubar sessões continuam na ficha, que o nome abre.
             */
            rowActions={(a): Action[] => [
              ...(canEdit
                ? [
                    {
                      label: 'Editar nome e e-mail',
                      icon: 'pessoa',
                      run: () => setEditingIdentityId(a.id),
                    } satisfies Action,
                  ]
                : []),
              ...(canInvite
                ? [
                    {
                      label:
                        a.situacao === 'convidado'
                          ? 'Reenviar o convite'
                          : 'Enviar o convite',
                      icon: 'sino',
                      disabled: a.blockedAt
                        ? 'Esta pessoa está bloqueada — o link não abriria nada.'
                        : (false as const),
                      run: () => invite.mutate(a),
                    } satisfies Action,
                  ]
                : []),
              ...(canBlock
                ? [
                    {
                      /* dispara no clique, sem confirmação — como em Usuários */
                      label: a.blockedAt ? 'Desbloquear o acesso' : 'Bloquear o acesso',
                      icon: 'bloqueado',
                      tone: a.blockedAt ? ('neutro' as const) : ('perigo' as const),
                      run: () => block.mutate({ id: a.id, blocked: !a.blockedAt }),
                    } satisfies Action,
                  ]
                : []),
            ]}
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (a) => (
                  <Link
                    to="/portal/members/$id"
                    params={{ id: a.id }}
                    className={linkTextClasses('font-medium pointer-coarse:px-0')}
                  >
                    {a.nome}
                  </Link>
                ),
              },
              {
                title: 'Contato',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (a) => (
                  <div className="min-w-0">
                    <p className="truncate">{a.email}</p>
                    <p className="truncate text-xs text-texto-suave">
                      {a.maskedCpf || 'CPF não informado'}
                    </p>
                  </div>
                ),
              },
              {
                title: 'Código no ERP',
                cell: (a) =>
                  a.erpCode ?? (
                    // o espelho entra na onda 3; até lá a ausência é comum e a
                    // tela diz que é ausência, não erro
                    <span className="text-texto-suave">não vinculado</span>
                  ),
              },
              {
                title: 'Situação',
                alignment: 'fim',
                cell: (a) => <MemberStatusBadge situacao={a.situacao} />,
              },
            ]}
          />

          <Pagination
            page={page}
            perPage={perPage}
            total={total ?? 0}
            onChange={setPage}
            onPerPageChange={setPerPage}
            label="associados"
            /* o cartão é `bleed`; o espaçamento é de quem entra nele */
            className="border-t border-borda-suave px-5 py-4"
          />
        </Card>
      )}

      {editingIdentityMember && (
        <Modal
          open
          onOpenChange={(next) => !next && setEditingIdentityId(null)}
          size="lg"
          title="Editar nome e e-mail"
          descricao={editingIdentityMember.nome}
        >
          <IdentityFields
            key={`${editingIdentityMember.nome}|${editingIdentityMember.email}`}
            initial={editingIdentityMember}
            save={async (body) =>
              ok(
                await api.members.edit({
                  params: { id: editingIdentityMember.id },
                  body,
                }),
                200,
                'Não foi possível salvar o nome e o e-mail.',
              )
            }
            saved={reload}
          />
        </Modal>
      )}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * Dar acesso a quem ainda não tem — `portal-43`
 * ------------------------------------------------------------------------- */

/** O que a busca no ERP devolveu, ou por que ela não devolveu nada. */
type Outcome =
  | { estado: 'achado'; candidate: ErpCandidate }
  | { estado: 'nao_existe' }
  | { estado: 'erp_mudo'; message: string };

/**
 * Procurar no ERP e criar o acesso.
 *
 * ## Os quatro desfechos são desenhados SEPARADOS, e é o ponto da tarefa
 *
 * Eles pedem ações opostas de quem está ao telefone:
 *
 * - **achou e dá para criar** — o botão aparece;
 * - **achou e não tem e-mail** — o botão não aparece, e a frase diz que o
 *   endereço precisa entrar no SGA. São 5.379 ativos da CE nessa situação
 *   (`portal-27`), e a plataforma tem um canal só;
 * - **não achou** — o CPF que a pessoa ditou não está na associação;
 * - **o ERP não respondeu** — não sabemos se está ou não.
 *
 * Os dois últimos são a distinção que mais importa: iguais na tela, mandam quem
 * atende reclamar da coisa errada — um é "confira o número que você me passou",
 * o outro é "o ERP está fora, tente daqui a pouco".
 *
 * E o quinto, que não é desfecho de busca e sim de cadastro: **já tem acesso**.
 * Aí a tela leva para a ficha, porque criar de novo faria o segundo cadastro de
 * quem já tem um.
 *
 * ## Criar não convida
 *
 * São dois atos, e a tela diz isso. O convite sai da ficha, e separá-los é o
 * que permite dar acesso a quem ainda vai receber o link em outro momento —
 * junto, o clique errado manda e-mail para alguém que não esperava.
 *
 * ## A SITUAÇÃO do ERP é mostrada e não recusa nada, e isso é regra 14
 *
 * O percurso de 10/09/2026 caiu num cadastro real com situação `CANCELADO`, e
 * a tela ofereceu criar assim mesmo. Não é descuido: `app.erp_status_classifications`
 * **não tem domínio de associado** — a casa classifica situação de boleto e de
 * veículo, e nunca disse o que os códigos de associado significam. Recusar por
 * `CANCELADO` seria escolher, aqui, uma regra de negócio que ninguém escreveu;
 * e a lista de quais códigos impedem acesso não existe em lugar nenhum.
 *
 * Então a situação aparece **ao lado do nome**, em texto, e quem está ao
 * telefone decide. O dia em que a casa classificar situação de associado, a
 * recusa nasce aqui com o nome dela — como `sem_email` nasceu.
 */
function GrantAccess({ created, toClose }: { created: () => void; toClose: () => void }) {
  const navigate = useNavigate();
  const [document, setDocument] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const digits = document.replace(/\D/g, '');
  const searchable = digits.length === 11 || digits.length === 14;

  const search = useMutation({
    mutationFn: async (): Promise<Outcome> => {
      const r = await api.members.searchInErp({ body: { document: digits } });
      if (r.status === 200) return { estado: 'achado', candidate: r.body };
      /*
       * O 404 é resposta, e não falha: o ERP disse que não tem ninguém com este
       * documento. Todo o resto é "não sei" — e o texto vem do servidor, que
       * sabe se foi credencial, disjuntor ou tempo esgotado.
       */
      if (r.status === 404) return { estado: 'nao_existe' };
      return {
        estado: 'erp_mudo',
        message: errorMessage(r.body, 'O ERP não respondeu a esta consulta.'),
      };
    },
    onSuccess: (r) => {
      setOutcome(r);
      setCreateError(null);
    },
    /*
     * A rede caindo não pode virar tela em branco.
     *
     * O `mutationFn` acima só devolve — quem lança aqui é o próprio `fetch`,
     * quando o pedido não chega a sair. Sem este ramo o cartão voltava ao
     * estado inicial depois do carregando, sem uma palavra, e quem atende
     * repetia a busca achando que não clicou. Cai em "não sei", que é a
     * verdade: a pergunta não chegou a ser respondida.
     */
    onError: (e: Error) => {
      setCreateError(null);
      setOutcome({
        estado: 'erp_mudo',
        message: e.message || 'A consulta não chegou a sair daqui.',
      });
    },
  });

  const create = useMutation({
    mutationFn: async (erpCode: string) => {
      const r = await api.members.create({ body: { erpCode } });
      if (r.status !== 201) {
        throw new Error(errorMessage(r.body, 'Não foi possível criar o acesso.'));
      }
      return r.body;
    },
    onSuccess: (member) => {
      created();
      setOutcome(null);
      setDocument('');
      // a ficha é onde o convite sai, e é para onde quem acabou de criar vai
      void navigate({ to: '/portal/members/$id', params: { id: member.id } });
    },
    onError: (e: Error) => setCreateError(e.message),
  });

  return (
    <Card
      title="Dar acesso a um associado"
      descricao="Procure pelo CPF ou CNPJ do cadastro no ERP."
      actions={
        <Button variant="discreto" size="sm" onClick={toClose}>
          Cancelar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setOutcome(null);
            setCreateError(null);
            search.mutate();
          }}
        >
          <div className="w-full max-w-xs">
            <Field
              label="CPF ou CNPJ"
              value={document}
              onChange={(e) => setDocument(e.target.value)}
              mask="document"
              /*
               * O CNPJ é dito aqui, e não descoberto na recusa. No SGA o campo
               * se chama `cpf` e guarda os dois — quem atende uma pessoa
               * jurídica precisa saber que pode digitar catorze dígitos antes
               * de tentar.
               */
              help="Pessoa jurídica entra pelo CNPJ, com catorze dígitos."
            />
          </div>
          <Button type="submit" loading={search.isPending} disabled={!searchable}>
            Procurar no ERP
          </Button>
        </form>

        {outcome?.estado === 'nao_existe' && (
          <Alert tone="alerta" title="Ninguém com este documento na associação.">
            O ERP respondeu, e não encontrou. Confira o número com o associado — e, se ele
            estiver certo, o cadastro ainda não existe no SGA.
          </Alert>
        )}

        {outcome?.estado === 'erp_mudo' && (
          <Alert tone="erro" title="Não sabemos: o ERP não respondeu.">
            {outcome.message} Isso <strong>não</strong> quer dizer que o documento não
            exista — tente de novo em alguns minutos.
          </Alert>
        )}

        {outcome?.estado === 'achado' && (
          <Found
            candidate={outcome.candidate}
            error={createError}
            creating={create.isPending}
            create={() => create.mutate(outcome.candidate.erpCode)}
          />
        )}
      </div>
    </Card>
  );
}

/** O cadastro que voltou, e o que dá para fazer com ele. */
function Found({
  candidate,
  error,
  creating,
  create,
}: {
  candidate: ErpCandidate;
  error: string | null;
  creating: boolean;
  create: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <DataList>
        <DataRow label="Nome">
          {candidate.nome ?? <NotProvided>sem nome no cadastro</NotProvided>}
        </DataRow>
        <DataRow label={documentLabel(candidate.document)}>
          {formatDocument(candidate.document) || <NotProvided>não informado</NotProvided>}
        </DataRow>
        <DataRow label="E-mail">
          {candidate.email ?? <NotProvided>não informado</NotProvided>}
        </DataRow>
        <DataRow label="Situação no ERP">
          {candidate.situacao.descricao ?? candidate.situacao.code ?? (
            <NotProvided>não informada</NotProvided>
          )}
        </DataRow>
        <DataRow label="Código no ERP">{candidate.erpCode}</DataRow>
      </DataList>

      {error && <Alert tone="erro">{error}</Alert>}

      {candidate.member ? (
        <Alert tone="informacao" title="Este associado já tem acesso.">
          O cadastro de <strong>{candidate.member.email}</strong> já está ligado a este
          código do ERP nesta base.{' '}
          <Link
            to="/portal/members/$id"
            params={{ id: candidate.member.id }}
            className={linkTextClasses()}
          >
            Abrir a ficha
          </Link>{' '}
          para convidar, bloquear ou ver as sessões.
        </Alert>
      ) : candidate.refusal === 'sem_email' ? (
        <Alert
          tone="alerta"
          title="Este cadastro não tem e-mail, e o convite sai por e-mail."
        >
          A plataforma tem um canal só. O endereço precisa ser cadastrado{' '}
          <strong>no SGA</strong> — depois disso, procure de novo por aqui.
        </Alert>
      ) : (
        <div>
          <Button loading={creating} onClick={create}>
            Criar acesso
          </Button>
          <p className="mt-2 text-sm text-texto-suave">
            O acesso nasce sem senha. O convite de primeiro acesso é enviado depois, pela
            ficha.
          </p>
        </div>
      )}
    </div>
  );
}

function labelTotal(total: number): string {
  return total === 1 ? '1 associado' : `${total} associados`;
}
